import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { DollarSign, Clock } from "lucide-react";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface AdjustmentBreakdown {
  backcharges: number;
  rework: number;
  disputedDebits: number;
  bonuses: number;
  net: number;
}

interface Props {
  contractAmount: number;
  billedToDate: number;
  retainagePct: number;
  retainageReleased: number;
  adjustments?: AdjustmentBreakdown;
  paidToDate?: number;
}

export const WOFinancialSummary = ({
  contractAmount,
  billedToDate,
  retainagePct,
  retainageReleased,
  adjustments,
  paidToDate = 0,
}: Props) => {
  const retainageHeld = billedToDate * (retainagePct / 100) - retainageReleased;
  const netBilled = billedToDate - retainageHeld;

  const adj = adjustments ?? { backcharges: 0, rework: 0, disputedDebits: 0, bonuses: 0, net: 0 };
  const totalDeductions = adj.backcharges + adj.rework + adj.disputedDebits;
  const netAdjustments = adj.bonuses - totalDeductions;

  const netPayableNow = netBilled + netAdjustments - paidToDate;
  const retainageBalance = contractAmount * (retainagePct / 100) - retainageReleased;
  const totalRemaining = Math.max(0, netPayableNow) + Math.max(0, retainageBalance);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" /> Financial Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm font-mono space-y-0">
        {/* Contract Amount */}
        <div className="flex justify-between font-semibold">
          <span>CONTRACT AMOUNT</span>
          <span>{fmt(contractAmount)}</span>
        </div>

        <Separator className="my-2" />

        {/* Billing section */}
        <div className="flex justify-between pl-2">
          <span className="text-muted-foreground">Billed to date</span>
          <span>{fmt(billedToDate)}</span>
        </div>
        <div className="flex justify-between pl-4 text-yellow-600">
          <span>Retainage held ({retainagePct}%)</span>
          <span>-{fmt(Math.max(0, retainageHeld))}</span>
        </div>
        <Separator className="my-1 ml-4" />
        <div className="flex justify-between pl-4">
          <span className="text-muted-foreground">Net billed (excl. retainage)</span>
          <span>{fmt(netBilled)}</span>
        </div>

        {/* Adjustments section */}
        {(adj.backcharges > 0 || adj.rework > 0 || adj.disputedDebits > 0 || adj.bonuses > 0) && (
          <>
            <div className="mt-3 mb-1">
              <span className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Adjustments</span>
            </div>
            {adj.backcharges > 0 && (
              <div className="flex justify-between pl-4 text-destructive">
                <span>Backcharges</span>
                <span>-{fmt(adj.backcharges)}</span>
              </div>
            )}
            {adj.rework > 0 && (
              <div className="flex justify-between pl-4 text-destructive">
                <span>Rework deductions</span>
                <span>-{fmt(adj.rework)}</span>
              </div>
            )}
            {adj.disputedDebits > 0 && (
              <div className="flex justify-between pl-4 text-yellow-600">
                <span className="flex items-center gap-1">
                  Disputed (pending) <Clock className="h-3 w-3" />
                </span>
                <span>-{fmt(adj.disputedDebits)}</span>
              </div>
            )}
            {adj.bonuses > 0 && (
              <div className="flex justify-between pl-4 text-green-600">
                <span>Bonuses/Credits</span>
                <span>+{fmt(adj.bonuses)}</span>
              </div>
            )}
            <Separator className="my-1 ml-4" />
            <div className="flex justify-between pl-4">
              <span className="text-muted-foreground">Net adjustments</span>
              <span className={netAdjustments >= 0 ? "text-green-600" : "text-destructive"}>
                {netAdjustments >= 0 ? "+" : ""}{fmt(netAdjustments)}
              </span>
            </div>
          </>
        )}

        <Separator className="my-2" />

        {/* Totals */}
        <div className="flex justify-between font-semibold">
          <span>Paid to date</span>
          <span>{fmt(paidToDate)}</span>
        </div>
        <div className="flex justify-between font-semibold text-green-600">
          <span>Net payable now</span>
          <span>{fmt(Math.max(0, netPayableNow))}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>Retainage balance</span>
          <span>{fmt(Math.max(0, retainageBalance))}</span>
        </div>

        <Separator className="my-2" />

        <div className="flex justify-between font-semibold">
          <span>TOTAL REMAINING (incl. retainage)</span>
          <span>{fmt(totalRemaining)}</span>
        </div>
      </CardContent>
    </Card>
  );
};
